// @ts-nocheck
import { useCallback } from 'react';
import { UserDetailsPayload } from '../../types';
import useLog from '../useLog';
import useUserDetails from '../user/useUserDetails';
import useAuth from '../auth/useAuth';
import { DiscordUser, DiscordGuild, DiscordAuth } from '../../types/resources';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';

export interface GetDiscordUserRsp {
  error: string;
  user?: DiscordUser;
  guilds?: DiscordGuild[];
  auth?: DiscordAuth;
}

export interface UpdateMemberPressDiscordReq {
  meta: {
    mepr_discord_username: string;
  };
}

interface UpdateDiscordRoleRsp {
  discordToken: string;
  memberpressId?: number;
}

const useDiscord = () => {
  const { initUserDetails } = useUserDetails();
  const { setToken } = useAuth();
  const { fetchAPIWithLog } = useLog('useDiscord');

  const updateMemberpressDiscord = useCallback(
    async (discordId: string): Promise<UserDetailsPayload> => {
      const payload = {
        meta: {
          mepr_discord_username: discordId,
        },
      };
      return saveDiscordAccountPayload(payload);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const saveDiscordAccountPayload = async (
    payload: UpdateMemberPressDiscordReq,
  ): Promise<UserDetailsPayload> => {
    const memberpressData = await fetchAPIWithLog<UserDetailsPayload>(
      `v1/me/user`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    );
    if (memberpressData.code != null) {
      setToken(null);
    }

    initUserDetails(memberpressData);
    return memberpressData;
  };

  const updateDiscordRole = useCallback(
    async ({ memberpressId, discordToken }: UpdateDiscordRoleRsp) => {
      const url = `discord/updateRole?auth=${discordToken}&userid=${memberpressId}`;
      return await fetchAPIWithLog<UpdateDiscordRoleRsp>(url, {
        method: 'POST',
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { updateMemberpressDiscord, updateDiscordRole };
};

export const useGetDiscordUser = <TData = GetDiscordUserRsp, TError = string>(
  {
    code,
    redir,
  }: {
    code?: string | null;
    redir?: string;
  },
  options?: Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'>,
) => {
  const { fetchAPIWithLog } = useLog('useGetDiscordUser');
  return useQuery<TData, TError>({
    queryKey: ['useGetDiscordUser', { code, redir }],
    queryFn: async (): Promise<TData> =>
      await fetchAPIWithLog<TData>(
        `discord_login_callback?code=${code!}&redir=${redir!}`,
      ),
    enabled: code != null && redir != null && redir !== '',
    ...options,
  });
};

export default useDiscord;
